import { useSyncExternalStore } from 'react';
import { AUTH_TOKEN_CHANGE_EVENT, getImpersonatorId } from '@/services/auth';

const TOKEN_STORAGE_KEY = 'auth_token';

// @MX:ANCHOR: [AUTO] useImpersonationActive - single source of truth for impersonation banner visibility
// @MX:REASON: Must stay in sync with the auth_token stored in localStorage across bfcache restores,
// back-navigation, cross-tab changes, and same-tab token swaps. React state in AuthContext is a
// secondary derivation; rendering the admin banner from state alone has produced a regression where
// the banner disappeared after navigating away to a different site and pressing the browser back
// button, even though the impersonation JWT was still present. Reading the JWT directly guarantees
// the banner is visible whenever the token carries an impersonator_id claim.

function subscribe(notify: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('storage', notify);
  window.addEventListener('pageshow', notify);
  window.addEventListener('focus', notify);
  window.addEventListener(AUTH_TOKEN_CHANGE_EVENT, notify);
  return () => {
    window.removeEventListener('storage', notify);
    window.removeEventListener('pageshow', notify);
    window.removeEventListener('focus', notify);
    window.removeEventListener(AUTH_TOKEN_CHANGE_EVENT, notify);
  };
}

function getSnapshot(): boolean {
  if (typeof window === 'undefined') return false;
  const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return false;
  return getImpersonatorId(token) != null;
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * Returns true whenever the auth_token currently stored in localStorage is an
 * impersonation JWT (carries impersonator_id). Backed by useSyncExternalStore
 * so the value stays in sync even when AuthContext state has not yet caught up
 * (bfcache restore, back navigation, cross-tab changes).
 */
export function useImpersonationActive(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
